import { DayFlag, DayPicker, SelectionState, UI } from "react-day-picker";
import * as React from "react";
import { format } from "date-fns";
import { cn } from "@gettactic/helpers";
import { useWeekStartsOn } from "@gettactic/hooks";
import { CalendarProps } from "./types";
import { CalendarRoot } from "./CalendarRoot";
import type { CustomComponents } from "react-day-picker";
import { ArrowLeftIcon, ArrowRightIcon } from "@cmp-src/icons/icons";

const classes = {
  DayPicker: {
    [UI.Root]:
      "select-none w-[290px] border border-neutral-300 rounded-lg text-black font-medium",
    [UI.Footer]:
      "mt-2 px-6 py-2 flex justify-between border-neutral-300 border-t text-sm",
    [UI.Months]: "flex relative flex-col space-y-4",
    [UI.Month]: "",
    [UI.MonthCaption]: "absolute top-1.5 left-8 right-8 flex justify-center",
    [UI.CaptionLabel]: "hidden",
    [UI.Nav]: "h-9 space-x-1 justify-between flex items-center relative",
    [UI.PreviousMonthButton]:
      "h-9 w-9 bg-transparent p-2 opacity-100 absolute left-1 cursor-pointer",
    [UI.NextMonthButton]:
      "h-9 w-9 bg-transparent p-2 opacity-100 absolute right-1 cursor-pointer",
    [UI.MonthGrid]: "w-full border-collapse space-y-1",
    [UI.Weekdays]: "flex",
    [UI.Weekday]: "text-muted-foreground w-full font-normal text-[0.8rem]",
    [UI.Week]: "flex w-full mt-2",
    [UI.Day]:
      "group inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 DAY-DEBUG text-center text-sm p-0 relative h-9 w-full focus-within:relative focus-within:z-20",
    [SelectionState.selected]: "selected",
    [DayFlag.today]: "today",
    [UI.DayButton]:
      "h-9 w-9 flex items-center justify-center rounded-full group-[.selected]:bg-primary group-[.selected]:text-primary-foreground group-[.today]:border-2 group-[.today]:border-primary group-[.today]:rounded-full group-[.range-middle.selected]:bg-transparent group-[.range-middle.selected]:text-accent-foreground hover:rounded-full hover:bg-primary-100 hover:text-primary-neutral-1000",

    [DayFlag.outside]:
      "day-outside text-muted-foreground aria-selected:text-muted-foreground",
    [DayFlag.disabled]: "text-muted-foreground opacity-50",
    [SelectionState.range_start]: "",
    [SelectionState.range_end]: "",
    [SelectionState.range_middle]:
      "range-middle aria-selected:bg-primary-200 aria-selected:text-accent-foreground aria-selected:rounded-none first:aria-selected:rounded-l-full last:aria-selected:rounded-r-full",
    [DayFlag.hidden]: "invisible",
    [UI.Dropdown]:
      "appearance-none indent-[1px] border-0 bg-none bg-transparent text-medium p-0 w-auto min-w-10 cursor-pointer",
    [UI.MonthsDropdown]: "text-center mr-1"
  },
  Chevron: "size-4",
  // A half band only joins days in the same week, so it is dropped when the
  // range starts on the last weekday column or ends on the first one
  RangeStart:
    "absolute inset-y-0 left-1/2 w-1/2 bg-primary-200 [:last-child>&]:hidden",
  RangeEnd:
    "absolute inset-y-0 right-1/2 w-1/2 bg-primary-200 [:first-child>&]:hidden",
  RangeSelectedWrapper: "relative"
};

export const Calendar: React.FC<CalendarProps> = (props) => {
  const {
    className,
    classNames,
    showOutsideDays = true,
    selectedDate,
    mode = "single",
    weekHover = false,
    size = "default",
    ...rest
  } = props;

  const sm = size === "sm";
  const weekStartsOn = useWeekStartsOn();

  const components: Partial<CustomComponents> = {
    Root: CalendarRoot,
    Chevron: ({ className, orientation, ...props }) =>
      orientation === "left" ? (
        <ArrowLeftIcon
          className={cn(sm ? "size-3" : classes.Chevron, className)}
          {...props}
        />
      ) : orientation === "right" ? (
        <ArrowRightIcon
          className={cn(sm ? "size-3" : classes.Chevron, className)}
          {...props}
        />
      ) : (
        <></>
      ),
    Day: ({ modifiers, children, day, ...props }) => {
      // A single-day range is both start and end: it has no band to join
      const isRangeStart = modifiers.range_start && !modifiers.range_end;
      const isRangeEnd = modifiers.range_end && !modifiers.range_start;

      return (
        <td {...props} className={cn(props.className)}>
          {/* Background for range_start */}
          {isRangeStart && <div className={classes.RangeStart} />}

          {/* Background for range_end */}
          {isRangeEnd && <div className={classes.RangeEnd} />}

          {/* The actual button */}
          <div className={classes.RangeSelectedWrapper}>{children}</div>
        </td>
      );
    }
  };

  const dayPickerClasses = {
    ...classes.DayPicker,
    ...(sm && {
      [UI.Root]:
        "font-inter border border-neutral-300 rounded-lg text-black font-medium text-xs",
      [UI.Months]: "font-inter  flex relative flex-col space-y-2",
      [UI.MonthCaption]:
        "font-inter  absolute top-1 left-6 right-6 flex justify-center",
      [UI.Nav]:
        "font-inter  h-7 space-x-1 justify-between flex items-center relative",
      [UI.PreviousMonthButton]:
        "font-inter h-7 w-7 bg-transparent p-1.5 opacity-100 absolute left-0.5 cursor-pointer",
      [UI.NextMonthButton]:
        "font-inter h-7 w-7 bg-transparent p-1.5 opacity-100 absolute right-0.5 cursor-pointer",
      [UI.MonthGrid]: "font-inter w-full border-collapse space-y-0.5",
      [UI.Weekday]:
        "font-inter text-muted-foreground w-full font-normal text-[0.65rem]",
      [UI.Week]: "font-inter flex w-full mt-1",
      [UI.Day]:
        "font-inter group inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-3 [&_svg]:shrink-0 DAY-DEBUG text-center text-xs p-0 relative h-7 w-full focus-within:relative focus-within:z-20",
      [UI.DayButton]:
        "font-inter h-7 w-7 flex items-center justify-center rounded-full group-[.selected]:bg-primary group-[.selected]:text-primary-foreground group-[.today]:border-1 group-[.today]:border-primary group-[.today]:rounded-full group-[.range-middle.selected]:bg-transparent group-[.range-middle.selected]:text-accent-foreground hover:rounded-full hover:bg-primary-100 hover:text-primary-neutral-1000 font-[400]"
    })
  };

  const properties: Omit<CalendarProps, "mode"> = {
    weekStartsOn,
    showOutsideDays,
    formatters: {
      formatWeekdayName: (date, options) => format(date, "ccccc", options)
    },
    className,
    classNames: {
      ...dayPickerClasses,
      [UI.Footer]: undefined,
      ...classNames
    },
    components,
    captionLayout: "dropdown",
    required: false,
    fixedWeeks: true,
    ...rest
  };

  if (mode === "single")
    return (
      <DayPicker
        mode={"single"}
        selected={
          Array.isArray(selectedDate)
            ? selectedDate[0]
            : selectedDate instanceof Date
              ? selectedDate
              : undefined
        }
        {...properties}
      />
    );

  if (mode === "range")
    return (
      <DayPicker
        mode={"range"}
        selected={
          selectedDate &&
          !Array.isArray(selectedDate) &&
          !(selectedDate instanceof Date)
            ? selectedDate
            : undefined
        }
        {...properties}
      />
    );

  if (mode === "multiple")
    return (
      <DayPicker
        mode={"multiple"}
        selected={Array.isArray(selectedDate) ? selectedDate : []}
        {...properties}
      />
    );

  return null;
};
